import { DEFAULT_LANGUAGE } from '../utils/defaultLanguage';

const DATA_BASE = `${process.env.PUBLIC_URL || ''}/data`;

const cache = new Map();
const inFlight = new Map();

function normalizeId(id) {
  if (id && typeof id === 'object' && id.$oid) {
    return id.$oid;
  }
  return id;
}

function normalizeItem(item) {
  if (!item || typeof item !== 'object') return item;
  return { ...item, _id: normalizeId(item._id) };
}

function normalizeData(data) {
  if (Array.isArray(data)) {
    return data.map(normalizeItem);
  }
  return normalizeItem(data);
}

async function loadJson(filename) {
  if (cache.has(filename)) {
    return cache.get(filename);
  }
  if (inFlight.has(filename)) {
    return inFlight.get(filename);
  }

  const promise = (async () => {
    const response = await fetch(`${DATA_BASE}/${filename}`);
    if (!response.ok) {
      throw new Error(`Failed to load ${filename} (${response.status})`);
    }

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      throw new Error(
        `Data file ${filename} is missing. Run "npm run sync-data" to copy JSON from data/ into public/data/.`
      );
    }

    const data = normalizeData(await response.json());
    cache.set(filename, data);
    return data;
  })();

  inFlight.set(filename, promise);
  try {
    return await promise;
  } finally {
    inFlight.delete(filename);
  }
}

function filterByLanguage(data, language) {
  return data.filter((item) => item.language === language);
}

function findById(data, id) {
  return data.find((item) => item._id === id);
}

function computeSpeakerStats(slokas) {
  const stats = {};
  slokas.forEach((sloka) => {
    stats[sloka.speaker] = (stats[sloka.speaker] || 0) + 1;
  });
  return Object.entries(stats).map(([speaker, count]) => ({ speaker, count }));
}

function computeChapterSpeakerStats(slokas) {
  const stats = {};
  slokas.forEach((sloka) => {
    const chapter = sloka.chapterNumber;
    const speaker = sloka.speaker;
    if (!stats[chapter]) stats[chapter] = {};
    stats[chapter][speaker] = (stats[chapter][speaker] || 0) + 1;
  });

  return Object.entries(stats)
    .map(([chapterNumber, speakers]) => ({
      chapterNumber: parseInt(chapterNumber, 10),
      speakers: Object.entries(speakers).map(([name, count]) => ({
        speaker: name,
        count
      }))
    }))
    .sort((a, b) => a.chapterNumber - b.chapterNumber);
}

export async function getChapters() {
  return loadJson('genai.chapter.json');
}

export async function getSlokas() {
  return loadJson('genai.sloka.json');
}

export async function getCommentaries() {
  return loadJson('genai.commentaries.json');
}

export async function getChaptersByLanguage(language) {
  const chapters = await getChapters();
  return filterByLanguage(chapters, language);
}

export async function getChapterById(id) {
  const chapters = await getChapters();
  return findById(chapters, id);
}

export async function getChapterByNumber(language, chapterNumber) {
  const chapters = await getChapters();
  return chapters.find(
    (c) => c.language === language && c.chapterNumber === chapterNumber
  );
}

export async function getSlokasByLanguage(language) {
  const slokas = await getSlokas();
  const englishSlokas = filterByLanguage(slokas, language);
  return englishSlokas.sort((a, b) => {
    if (a.chapterNumber !== b.chapterNumber) {
      return a.chapterNumber - b.chapterNumber;
    }
    return a.slokaNumber - b.slokaNumber;
  });
}

export async function getSlokaById(id) {
  const slokas = await getSlokas();
  return findById(slokas, id);
}

export async function getSlokasByChapter(language, chapterNumber) {
  const slokas = await getSlokas();
  return slokas.filter(
    (s) => s.language === language && s.chapterNumber === chapterNumber
  );
}

export async function getSlokasBySpeaker(language, speaker, chapterNumber) {
  const slokas = await getSlokasByLanguage(language);
  return slokas.filter((s) => {
    if (s.speaker !== speaker) return false;
    if (chapterNumber != null && s.chapterNumber !== chapterNumber) return false;
    return true;
  });
}

export async function getSpeakers() {
  const slokas = await getSlokasByLanguage(DEFAULT_LANGUAGE);
  const speakers = [...new Set(slokas.map((s) => s.speaker))];
  return speakers.map((speaker) => ({ speaker }));
}

export async function getSpeakerStats() {
  const slokas = await getSlokasByLanguage(DEFAULT_LANGUAGE);
  return computeSpeakerStats(slokas);
}

export async function getChapterSpeakerStats() {
  const slokas = await getSlokasByLanguage(DEFAULT_LANGUAGE);
  return computeChapterSpeakerStats(slokas);
}

export async function getCommentariesBySloka(language, chapterNumber, slokaNumber) {
  const commentaries = await getCommentaries();
  return commentaries.filter(
    (c) =>
      c.language === language &&
      c.chapterNumber === chapterNumber &&
      c.slokaNumber === slokaNumber
  );
}

export async function getSynonymsByChapter(language, chapterNumber) {
  const all = await loadJson('genai.synonym.json');
  return all
    .filter((r) => r.language === language && r.chapterNumber === chapterNumber)
    .sort((a, b) => a.slokaNumber - b.slokaNumber || a.sequence - b.sequence);
}

export async function getMbhData() {
  const [family, relationships, narrative] = await Promise.all([
    loadJson('genai.mbh.family.json'),
    loadJson('genai.mbh.relationships.json'),
    loadJson('genai.mbh.narrative.json'),
  ]);
  return {
    people: family.people || [],
    families: family.families || [],
    children: family.children || [],
    parentage: relationships.parentage || [],
    narrative: narrative.items || [],
  };
}
